import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDateRangeSelectMenu {
  menuIsOpen: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  handleMenuOpen: () => void;
  handleMenuClose: () => void;
  handleMenuMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    menuOnMouseDown?: React.MouseEventHandler<HTMLDivElement>,
  ) => void;
}

export const useDateRangeSelectMenu = (): UseDateRangeSelectMenu => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isNativeSelectInteraction = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMenuOpen = useCallback((): void => setMenuIsOpen(true), []);

  const handleMenuClose = useCallback((): void => {
    if (isNativeSelectInteraction.current) {
      isNativeSelectInteraction.current = false;
      return;
    }
    setMenuIsOpen(false);
  }, []);

  useEffect(() => {
    if (!menuIsOpen) return undefined;

    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuIsOpen]);

  const handleMenuMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, menuOnMouseDown?: React.MouseEventHandler<HTMLDivElement>): void => {
      if ((e.target as HTMLElement).tagName === 'SELECT') {
        isNativeSelectInteraction.current = true;
        e.stopPropagation();
        return;
      }
      menuOnMouseDown?.(e);
    },
    [],
  );

  return {
    menuIsOpen,
    containerRef,
    handleMenuOpen,
    handleMenuClose,
    handleMenuMouseDown,
  };
};
